/**
 * Every `urn:rythmo:problem:*` type the platform can answer with (ADR-0026). A
 * client may switch on `type`; `detail` is prose that changes. These lived
 * inside billing-svc until M6b moved them here: a typed client for the Expo
 * app and the admin SPA cannot import a constant that lives inside a service.
 */
// --- Identity and school selection ----------------------------------------
/**
 * The caller named a school in `X-Rythmo-School` that their token holds no
 * grant at ([ADR-0049](../../../docs/decisions/0049-a-request-names-its-school-the-token-proves-the-grant.md)).
 *
 * A `403`, not a `404`: the token is valid and the school may well exist. Saying
 * "not found" would leak nothing useful and would mislead a client that can see
 * the school in its own list.
 */
export const NO_GRANT_AT_SCHOOL = 'urn:rythmo:problem:no-grant-at-school';
/**
 * The caller holds grants at more than one school and named none.
 *
 * A `400` rather than a guess. Defaulting to the resource-owner organization
 * would silently act at a teacher's *home* school while they are working at
 * another, which is the one failure this plan exists to prevent.
 */
export const SCHOOL_NOT_NAMED = 'urn:rythmo:problem:school-not-named';
// --- Booking --------------------------------------------------------------
/**
 * The member has no entitlement left, and no taster to fall back on.
 *
 * **There is deliberately no `class-full`.** A full session *waitlists*
 * ([`plan-4`](../../../docs/planning/plan-4-booking-attendance.md)), so being
 * full is a `201` with `status: waitlisted`, not a refusal. This is the only
 * hard reject on the booking path.
 */
export const NO_CREDITS = 'urn:rythmo:problem:no-credits';
/**
 * A second free taster. Raised from the **partial unique index**, not only the
 * history check beside it: a member with no membership takes no lock in the
 * keystone, so two concurrent taster requests can both pass that check before
 * either commits.
 */
export const TASTER_ALREADY_USED = 'urn:rythmo:problem:taster-already-used';
/** The member already holds a live booking on this session. */
export const ALREADY_BOOKED = 'urn:rythmo:problem:already-booked';
/**
 * The caller holds no membership row at this school.
 *
 * A `404` rather than a `403`: whether a person exists at some other school is
 * not this school's to learn.
 */
export const NOT_A_MEMBER = 'urn:rythmo:problem:not-a-member';
/** No such booking for this caller, or it is already cancelled. */
export const BOOKING_NOT_CANCELLABLE = 'urn:rythmo:problem:booking-not-cancellable';
/**
 * The booking holds no live claim on a seat: it was never offered one, it has
 * already been claimed, or the hold ran out and moved on
 * ([ADR-0055](../../../docs/decisions/0055-a-waitlist-hold-reserves-the-seat-and-is-claimed.md)).
 * A claim refused for want of credits is `no-credits` instead, and keeps the hold.
 */
export const BOOKING_NOT_CLAIMABLE = 'urn:rythmo:problem:booking-not-claimable';
/** The session is cancelled, or has already started. */
export const SESSION_NOT_BOOKABLE = 'urn:rythmo:problem:session-not-bookable';
/**
 * A partner-balanced session needs the booking to name a side
 * ([ADR-0008](../../../docs/decisions/0008-lead-follow-capacity.md)) — a lead and a
 * follow reserve from different counters and wait on different queues.
 */
export const DANCE_ROLE_REQUIRED = 'urn:rythmo:problem:dance-role-required';
/** A plain-headcount session has one pool, so naming a side is a client bug. */
export const DANCE_ROLE_NOT_ACCEPTED = 'urn:rythmo:problem:dance-role-not-accepted';
// --- Connect and school state ---------------------------------------------
/**
 * One `type` for both "never connected" (`stripe_account_id IS NULL`) and
 * "connected but not yet `active`" (still `pending`, or regressed). A client
 * has exactly one corrective action either way — the owner finishes Stripe
 * Connect onboarding — so there is nothing for a second URN to distinguish.
 */
export const SCHOOL_CANNOT_ACCEPT_PAYMENTS = 'urn:rythmo:problem:school-cannot-accept-payments';
// --- Checkout and entitlement ----------------------------------------------
export const MEMBERSHIP_ALREADY_ACTIVE = 'urn:rythmo:problem:membership-already-active';
/**
 * `entitlements_json` defaults to `{}` and nothing requires a `quota` key
 * (`CreateMembershipPlanDto.entitlements` is `Record<string, unknown>`,
 * unvalidated). A pass plan with no quota would otherwise let a member pay and
 * receive nothing to book — the webhook's insert branch has no quota to grant
 * — or, on a top-up, would add SQL NULL to an existing grant and erase it,
 * including an offline comp's explicit allowance.
 *
 * Refused here, before any Stripe object exists, rather than defended against
 * in the webhook: `entitlements_json` is immutable after a plan is published
 * (`UpdateMembershipPlanDto` carries no such field), so a plan this check lets
 * through can never later reach the webhook without a quota.
 */
export const PLAN_HAS_NO_QUOTA = 'urn:rythmo:problem:plan-has-no-quota';
/**
 * ADR-0041's mirror image of {@link MEMBERSHIP_ALREADY_ACTIVE}: a member who
 * holds nothing has nothing to top up. The `type` is branchable so a client
 * can route this member to `/checkout-session` instead of showing a dead end
 * — the same courtesy `MEMBERSHIP_ALREADY_ACTIVE` extends in the other
 * direction.
 */
export const NO_ACTIVE_MEMBERSHIP = 'urn:rythmo:problem:no-active-membership';
/**
 * The held membership is real but has no `plan_id` — an offline comp granted
 * with `granted_quota` and no plan (`offline-membership.service.ts`, M2). It
 * has no plan whose allotment a top-up could re-buy: ADR-0041 defines a
 * top-up as "the same plan's allotment again, at the same price", and there
 * is no plan to read a price or a quota from.
 */
export const MEMBERSHIP_HAS_NO_PLAN = 'urn:rythmo:problem:membership-has-no-plan';
/**
 * The held plan has been soft-deleted since the membership was granted — an
 * ordinary event (a school retiring a plan its members still hold), not a
 * not-found. Distinct from `readPurchasableBuyer`'s generic
 * `NotFoundException` for the same underlying condition on the **buy** path:
 * there, the caller named a planId that turned out not to exist, and 404 is
 * honest. Here, the caller named nothing — the plan came from their own
 * membership — so a 404 would misleadingly suggest they asked for something
 * that never existed.
 */
export const PLAN_RETIRED = 'urn:rythmo:problem:plan-retired';
/**
 * ADR-0041: "Passes only. A recurring membership that renews would freeze a
 * stale plan quota into the row; those members take the plan-change path
 * instead." Checked here, in `assertMayTopUp`, rather than left to
 * `ensurePrice`, which since M4 happily mints a recurring Price — so without
 * this check a comped member on a recurring plan would be sold a subscription
 * through the top-up path. It is reachable today, because
 * `offline-membership.service.ts`'s `grant()` validates only that the target
 * plan exists (no `billing_mode`, no `deleted_at` filter), so a comped member
 * can already be holding a recurring plan. A member in that state needs a
 * branchable refusal telling them where to go, not an opaque server error.
 */
export const PLAN_IS_RECURRING = 'urn:rythmo:problem:plan-is-recurring';
/**
 * A recurring plan the school configured with `trial_period_days`. A trial is
 * not in M4's scope, and the alternative to refusing — opening a Session that
 * ignores it — would charge a member at once for a plan the school advertised
 * with a free period. Refused before any Stripe object exists; recorded in the
 * Plan 3 ground file as a known gap.
 */
export const PLAN_TRIAL_NOT_SUPPORTED = 'urn:rythmo:problem:plan-trial-not-supported';
/**
 * ADR-0045: a member whose Stripe-billed membership is `past_due` or `unpaid`
 * owes money on it. Their path is paying that (Stripe's hosted invoice page),
 * not a second Subscription — refused before any Stripe object exists, with a
 * branchable type so a client can route them to the right place.
 */
export const MEMBERSHIP_PAYMENT_OVERDUE = 'urn:rythmo:problem:membership-payment-overdue';
/**
 * ADR-0041's "holding nothing" mirror image: a member with no bookable or
 * overdue row has no membership to show at all. Distinct from
 * `NO_ACTIVE_MEMBERSHIP`, which is the top-up route's 409 — this is a 404,
 * and this route never writes anything a client could be refused from doing.
 */
export const NO_MEMBERSHIP = 'urn:rythmo:problem:no-membership';
/**
 * Thrown when a membership is in some status other than `'active'` or
 * `'cancelled'` when a caller tries to cancel it — an offline membership
 * already `expired` or otherwise settled is refused rather than silently
 * rewritten.
 */
export const MEMBERSHIP_NOT_CANCELLABLE = 'urn:rythmo:problem:membership-not-cancellable';
/**
 * The member has never paid through Stripe at this school, so there is no
 * Customer for a Billing Portal to open on. `no-membership`'s shape: a 404,
 * because there is nothing of theirs to show.
 */
export const NO_BILLING_ACCOUNT = 'urn:rythmo:problem:no-billing-account';
// --- Plan changes ------------------------------------------------------------
/**
 * The refusals a member can act on when changing plan
 * ([ADR-0046](../../../docs/decisions/0046-a-plan-change-is-charged-now-only-when-it-only-adds.md)).
 * Each is branchable (ADR-0026); `detail` is prose that may change.
 */
export const MEMBERSHIP_NOT_RECURRING = 'urn:rythmo:problem:membership-not-recurring';
export const PLAN_NOT_RECURRING = 'urn:rythmo:problem:plan-not-recurring';
export const PLAN_UNCHANGED = 'urn:rythmo:problem:plan-unchanged';
export const PLAN_CHANGE_QUEUED = 'urn:rythmo:problem:plan-change-queued';
export const PLAN_CHANGE_AWAITING_PAYMENT = 'urn:rythmo:problem:plan-change-awaiting-payment';
export const NO_PLAN_CHANGE_QUEUED = 'urn:rythmo:problem:no-plan-change-queued';
/**
 * An upgrade asked for inside a free period (ADR-0046 amendment 2). Stripe
 * prorates nothing while a Subscription is trialing, so it would apply for
 * free. Refused until the period already paid for ends.
 */
export const PLAN_UPGRADE_WAITS_FOR_RENEWAL = 'urn:rythmo:problem:plan-upgrade-waits-for-renewal';
/**
 * An earlier change Stripe has already applied is still reaching the
 * membership (ADR-0046 amendment 2), so the change must be retried shortly.
 * Refused because the membership's plan, and its billing interval, are not yet
 * the ones Stripe bills: a change classified against them could be mis-sent.
 */
export const PLAN_CHANGE_SETTLING = 'urn:rythmo:problem:plan-change-settling';
/**
 * The Stripe Subscription has no item to change — its shape was edited outside
 * Rythmo, in the school's Dashboard.
 */
export const SUBSCRIPTION_NOT_CHANGEABLE = 'urn:rythmo:problem:subscription-not-changeable';
// --- Refunds and disputes ----------------------------------------------------
/**
 * This membership has no payment Rythmo can refund: it was granted before M6b
 * stored handles, it is an offline comp, or the payment is older than the one
 * handle each membership keeps (ADR-0044's 2026-09-17 amendment). The Stripe
 * Dashboard can still refund it — and keeps the transaction fee with Rythmo,
 * which is why the `detail` says so.
 */
export const MEMBERSHIP_NOT_REFUNDABLE = 'urn:rythmo:problem:membership-not-refundable';
/** The amount asked for is more than the payment can still return. */
export const REFUND_EXCEEDS_PAYMENT = 'urn:rythmo:problem:refund-exceeds-payment';
// --- Stripe errors -----------------------------------------------------------
/** The member's card was refused. `detail` is Stripe's message, written for the cardholder. */
export const PAYMENT_DECLINED = 'urn:rythmo:problem:payment-declined';
/** Stripe is rate-limiting, unreachable or failing on its side. Retry after `Retry-After`. */
export const PAYMENT_PROVIDER_UNAVAILABLE = 'urn:rythmo:problem:payment-provider-unavailable';
/** Stripe refused the request for a reason the member cannot act on. Logged at error. */
export const PAYMENT_PROVIDER_ERROR = 'urn:rythmo:problem:payment-provider-error';
/**
 * Every problem type this platform can answer with. A client may switch on any
 * of them; `detail` is prose that changes.
 */
export const PROBLEM_TYPES = [
    NO_CREDITS,
    TASTER_ALREADY_USED,
    ALREADY_BOOKED,
    SESSION_NOT_BOOKABLE,
    NOT_A_MEMBER,
    BOOKING_NOT_CANCELLABLE,
    BOOKING_NOT_CLAIMABLE,
    DANCE_ROLE_REQUIRED,
    DANCE_ROLE_NOT_ACCEPTED,
    NO_GRANT_AT_SCHOOL,
    SCHOOL_NOT_NAMED,
    SCHOOL_CANNOT_ACCEPT_PAYMENTS,
    MEMBERSHIP_ALREADY_ACTIVE,
    PLAN_HAS_NO_QUOTA,
    NO_ACTIVE_MEMBERSHIP,
    MEMBERSHIP_HAS_NO_PLAN,
    PLAN_RETIRED,
    PLAN_IS_RECURRING,
    PLAN_TRIAL_NOT_SUPPORTED,
    MEMBERSHIP_PAYMENT_OVERDUE,
    NO_MEMBERSHIP,
    MEMBERSHIP_NOT_CANCELLABLE,
    NO_BILLING_ACCOUNT,
    MEMBERSHIP_NOT_RECURRING,
    PLAN_NOT_RECURRING,
    PLAN_UNCHANGED,
    PLAN_CHANGE_QUEUED,
    PLAN_CHANGE_AWAITING_PAYMENT,
    NO_PLAN_CHANGE_QUEUED,
    PLAN_UPGRADE_WAITS_FOR_RENEWAL,
    PLAN_CHANGE_SETTLING,
    SUBSCRIPTION_NOT_CHANGEABLE,
    MEMBERSHIP_NOT_REFUNDABLE,
    REFUND_EXCEEDS_PAYMENT,
    PAYMENT_DECLINED,
    PAYMENT_PROVIDER_UNAVAILABLE,
    PAYMENT_PROVIDER_ERROR,
];
//# sourceMappingURL=problems.js.map